const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const fieldValue = (value) => {
  const trimmed = String(value ?? "").trim();
  return trimmed && trimmed !== "0000-00-00" ? escapeHtml(trimmed) : "&nbsp;";
};

const formatDate = (value) => {
  const text = String(value ?? "").trim();
  if (!text || text === "0000-00-00") return "";

  const parsedDate = new Date(text);
  if (Number.isNaN(parsedDate.getTime())) return text;

  return parsedDate.toLocaleDateString("en-PH", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const genderLabel = (value) => {
  const text = String(value ?? "").trim().toLowerCase();
  if (text === "m" || text === "male") return "Male";
  if (text === "f" || text === "female") return "Female";
  return text;
};

const joinValues = (values) =>
  values
    .map((value) => String(value ?? "").trim())
    .filter(Boolean)
    .join(", ");

const getHomeAddress = (physicianData) =>
  joinValues([
    physicianData?.home_streetno_bldg,
    physicianData?.home_barangay,
    physicianData?.home_towncity,
    physicianData?.home_province,
    physicianData?.home_postalcode,
    physicianData?.home_region,
  ]);

const getProvincialAddress = (physicianData) =>
  joinValues([
    physicianData?.provincial_streetno_bldg,
    physicianData?.provincial_barangay,
    physicianData?.provincial_towncity,
    physicianData?.provincial_province,
    physicianData?.provincial_postalcode,
    physicianData?.provincial_region,
  ]);

const resolveLogoUrl = () => {
  if (typeof window === "undefined") return "/amaphil-logo-350.png";
  const origin = window.location?.origin || "";
  return `${origin}/amaphil-logo-350.png`;
};

const affiliationRowsHtml = (affiliations = []) => {
  if (!Array.isArray(affiliations) || affiliations.length === 0) {
    return `
      <tr>
        <td colspan="5" class="empty-row">No provider affiliations added.</td>
      </tr>
    `;
  }

  return affiliations
    .map(
      (affiliation) => `
        <tr>
          <td>${fieldValue(affiliation?.provider_code)}</td>
          <td>${fieldValue(affiliation?.provider_name)}</td>
          <td>${fieldValue(affiliation?.room_no)}</td>
          <td>${fieldValue(affiliation?.schedule)}</td>
          <td>${fieldValue(affiliation?.remarks)}</td>
        </tr>
      `
    )
    .join("");
};

const buildPhysicianPrintHtml = ({ physicianData, physicianId }) => {
  const generationDate = new Date().toLocaleDateString("en-PH", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  const logoUrl = resolveLogoUrl();
  const physicianName = joinValues([
    physicianData?.last_name,
    physicianData?.first_name,
    physicianData?.middle_name,
  ]);

  return `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>Medical Doctor Accreditation Form - ${fieldValue(physicianName || physicianId)}</title>
  <style>
    :root { color-scheme: light; }
    * { box-sizing: border-box; }
    body {
      margin: 0;
      font-family: "Open Sans", "Segoe UI", Arial, sans-serif;
      background: #eef1f5;
      color: #333;
      line-height: 1.35;
      padding: 24px 16px;
      font-size: 12px;
    }
    .sheet {
      max-width: 1050px;
      margin: 0 auto;
      background: #fff;
      border: 1px solid #e5e7eb;
      box-shadow: 0 18px 40px rgba(15, 23, 42, 0.10);
    }
    .letter-pad { padding: 20px; }
    .invoice-head {
      display: grid;
      grid-template-columns: 7fr 5fr;
      gap: 18px;
      margin-bottom: 10px;
    }
    .invoice-logo img {
      max-width: 60%;
      height: auto;
      display: block;
    }
    .company-address {
      text-align: right;
      font-size: 12px;
      color: #4b5563;
      line-height: 1.55;
    }
    .bold { font-weight: 700; }
    .uppercase { text-transform: uppercase; }
    .title-bar {
      background: #26c281;
      color: #fff;
      text-align: center;
      padding: 8px;
      margin: 12px 0 14px;
      font-size: 15px;
      font-weight: 700;
      letter-spacing: .04em;
    }
    .section-title {
      padding: 6px 10px;
      border: 2px solid #26c281;
      color: #26c281;
      text-align: center;
      font-style: italic;
      font-weight: 700;
      letter-spacing: .04em;
      text-transform: uppercase;
      margin: 14px 0 10px;
    }
    .grid {
      display: grid;
      grid-template-columns: repeat(12, minmax(0, 1fr));
      border: 1px solid #999;
      border-bottom: 0;
    }
    .cell {
      min-height: 52px;
      border-right: 1px solid #999;
      border-bottom: 1px solid #999;
      padding: 8px;
    }
    .cell:last-child { border-right: 0; }
    .span-1 { grid-column: span 1; }
    .span-2 { grid-column: span 2; }
    .span-3 { grid-column: span 3; }
    .span-4 { grid-column: span 4; }
    .span-5 { grid-column: span 5; }
    .span-6 { grid-column: span 6; }
    .span-7 { grid-column: span 7; }
    .span-8 { grid-column: span 8; }
    .span-9 { grid-column: span 9; }
    .span-12 { grid-column: span 12; }
    .value {
      min-height: 18px;
      color: #111827;
      font-weight: 600;
      word-break: break-word;
    }
    .label {
      margin-top: 8px;
      color: #4b5563;
      font-size: 10px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: .03em;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      font-size: 12px;
    }
    th, td {
      border: 1px solid #999;
      padding: 8px;
      text-align: left;
      vertical-align: top;
    }
    th {
      background: #f3f4f6;
      color: #374151;
      text-transform: uppercase;
      font-size: 10px;
      letter-spacing: .04em;
    }
    .empty-row {
      text-align: center;
      color: #6b7280;
      padding: 16px;
    }
    .attestation {
      margin-top: 16px;
      text-indent: 42px;
      text-align: justify;
      color: #374151;
    }
    .signature {
      margin-top: 24px;
      display: flex;
      justify-content: flex-end;
      text-align: center;
      color: #374151;
    }
    .signature-lines {
      width: 280px;
      line-height: 1.8;
    }
    .print-actions { margin-top: 24px; }
    .print-btn {
      background: #26c281;
      color: #fff;
      padding: 10px 24px;
      border: 0;
      border-radius: 4px;
      font-weight: 700;
      cursor: pointer;
      text-transform: uppercase;
      letter-spacing: .04em;
    }
    @media print {
      body { background: #fff; padding: 0; }
      .sheet { border: 0; box-shadow: none; max-width: none; }
      .letter-pad { padding: 12mm 10mm; }
      .print-actions { display: none !important; }
      .grid, table, .section-title { break-inside: avoid; }
    }
  </style>
</head>
<body>
  <div class="sheet">
    <div class="letter-pad">
      <div class="invoice-head">
        <div class="invoice-logo">
          <img src="${escapeHtml(logoUrl)}" alt="AMAPHIL Logo" onerror="this.style.display='none'" />
        </div>
        <div class="company-address">
          <span class="bold uppercase">Advanced Medical Access Philippines, Inc.</span><br/>
          820 JP Rizal Street<br/>
          Barangay Poblacion, Makati City<br/>
          <span class="bold">T</span> (02) 8241-7679 | (02) 8355-7500<br/>
          <span class="bold">E</span> info@amaphil.com.ph<br/>
          <span class="bold">W</span> www.amaphil.com.ph<br/>
          <span>Date:</span> <span class="bold">${generationDate}</span>
        </div>
      </div>

      <div class="title-bar">Medical Doctor Accreditation Form</div>

      <div class="section-title">I. Basic Information</div>
      <div class="grid">
        <div class="cell span-4"><div class="value">${fieldValue(physicianData?.last_name)}</div><div class="label">Last Name</div></div>
        <div class="cell span-4"><div class="value">${fieldValue(physicianData?.first_name)}</div><div class="label">Given Name</div></div>
        <div class="cell span-4"><div class="value">${fieldValue(physicianData?.middle_name)}</div><div class="label">Middle Name</div></div>
        <div class="cell span-3"><div class="value">${fieldValue(formatDate(physicianData?.date_of_birth))}</div><div class="label">Birthday</div></div>
        <div class="cell span-2"><div class="value">${fieldValue(genderLabel(physicianData?.gender))}</div><div class="label">Gender</div></div>
        <div class="cell span-2"><div class="value">${fieldValue(physicianData?.age)}</div><div class="label">Age</div></div>
        <div class="cell span-2"><div class="value">${fieldValue(physicianData?.civil_status)}</div><div class="label">Civil Status</div></div>
        <div class="cell span-3"><div class="value">${fieldValue(physicianData?.mothers_name)}</div><div class="label">Full Mother's Maiden Name</div></div>
        <div class="cell span-6"><div class="value">${fieldValue(getHomeAddress(physicianData))}</div><div class="label">Home Address</div></div>
        <div class="cell span-6"><div class="value">${fieldValue(getProvincialAddress(physicianData))}</div><div class="label">Provincial Address</div></div>
        <div class="cell span-12"><div class="value">${fieldValue(joinValues([physicianData?.specialization, physicianData?.sub_specialization]))}</div><div class="label">Type of Doctor or Specialization/s</div></div>
      </div>

      <div class="section-title">II. Contact Details and Licenses</div>
      <div class="grid">
        <div class="cell span-4"><div class="value">${fieldValue(physicianData?.tel_no)}</div><div class="label">Telephone Number</div></div>
        <div class="cell span-4"><div class="value">${fieldValue(physicianData?.mobile_no)}</div><div class="label">Mobile Number</div></div>
        <div class="cell span-4"><div class="value">${fieldValue(physicianData?.email_add)}</div><div class="label">Email Address</div></div>
        <div class="cell span-6"><div class="value">${fieldValue(physicianData?.tin_no)}</div><div class="label">TIN Number</div></div>
        <div class="cell span-6"><div class="value">${fieldValue(physicianData?.sss_no)}</div><div class="label">SSS / GSIS Number</div></div>
        <div class="cell span-6"><div class="value">${fieldValue(physicianData?.prc_no)}</div><div class="label">PRC License Number</div></div>
        <div class="cell span-6"><div class="value">${fieldValue(formatDate(physicianData?.prc_expiry))}</div><div class="label">PRC Validity Date</div></div>
        <div class="cell span-4"><div class="value">${fieldValue(physicianData?.phic_no)}</div><div class="label">PHIC Number</div></div>
        <div class="cell span-4"><div class="value">${fieldValue(formatDate(physicianData?.phic_from))}</div><div class="label">PHIC Date From</div></div>
        <div class="cell span-4"><div class="value">${fieldValue(formatDate(physicianData?.phic_to))}</div><div class="label">PHIC Date To</div></div>
      </div>

      <div class="section-title">III. Provider Affiliation</div>
      <table>
        <thead>
          <tr>
            <th>Provider Code</th>
            <th>Provider Name</th>
            <th>Room Number</th>
            <th>Clinic Schedule</th>
            <th>Remarks</th>
          </tr>
        </thead>
        <tbody>
          ${affiliationRowsHtml(physicianData?.provider_affiliations)}
        </tbody>
      </table>

      <p class="attestation">
        I hereby attest that all above information are true and correct to my own knowledge. I hereby authorize
        Advanced Medical Access Philippines, Inc. to validate any of the above given information from whatever source
        it may consider appropriate. Any misrepresentation on the above information shall constitute a just ground for
        the rejection of my application or the termination of my contract with the Company.
      </p>

      <div class="signature">
        <div class="signature-lines">
          ______________________________<br/>
          <strong>Signature of Applicant</strong><br/><br/>
          Date: ________________________
        </div>
      </div>

      <div class="print-actions">
        <button type="button" class="print-btn" onclick="window.print()">Print</button>
      </div>
    </div>
  </div>
</body>
</html>`;
};

export const openPhysicianPrintPreview = ({ physicianData, physicianId }) => {
  if (typeof window === "undefined") return false;

  const printWindow = window.open("", "_blank", "width=1200,height=900");
  if (!printWindow) return false;

  const html = buildPhysicianPrintHtml({
    physicianData: physicianData || {},
    physicianId: physicianId || "",
  });

  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.focus();
  return true;
};
